import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, WORKER_API_BASE } from '../lib/api';
import { useToast } from '../components/Toast';
import { Upload, Download, Trash2, Search, Pencil, X, Check, File, FileText, FileSpreadsheet, Image, FolderOpen, Folder, ChevronRight, ChevronDown, Zap } from 'lucide-react';
import SupervisorPasswordModal from '../components/SupervisorPasswordModal';
import { useAuth } from '../contexts/AuthContext';
import { tr } from '../lib/i18nHelpers';

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function fileIcon(type: string) {
  if (type.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
  if (type.includes('sheet') || type.includes('excel') || type.includes('xls') || type.includes('csv')) return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
  if (type.includes('image') || type.includes('png') || type.includes('jpg')) return <Image className="h-5 w-5 text-blue-500" />;
  return <File className="h-5 w-5 text-gray-500" />;
}

function autoFolder(filename: string, fileType: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const t = (fileType || '').toLowerCase();
  if (t.includes('pdf') || ext === 'pdf') return 'Documents/PDF';
  if (t.includes('word') || ext === 'doc' || ext === 'docx') return 'Documents/Word';
  if (t.includes('sheet') || t.includes('excel') || ext === 'xls' || ext === 'xlsx') return 'Spreadsheets';
  if (t.includes('csv') || ext === 'csv') return 'Spreadsheets/CSV';
  if (t.includes('image') || ext === 'png' || ext === 'jpg' || ext === 'jpeg' || ext === 'gif' || ext === 'webp') return 'Images';
  if (ext === 'zip' || ext === 'rar' || ext === '7z') return 'Archives';
  if (ext === 'txt') return 'Documents/Text';
  if (ext === 'ppt' || ext === 'pptx') return 'Documents/Slides';
  return 'Other';
}

async function downloadFile(id: string, filename: string) {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/file-storage/${id}/download`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface FileItem {
  id: string;
  filename: string;
  original_name?: string;
  file_type: string;
  file_size: number;
  folder: string;
  description?: string;
  category?: string;
  direction?: string;
  payment_status?: string;
  amount?: number;
  created_at: string;
}

interface TreeNode {
  name: string;
  path: string;
  files: FileItem[];
  children: TreeNode[];
}

function buildTree(files: FileItem[]): TreeNode {
  const root: TreeNode = { name: 'All Files', path: '', files: [], children: [] };
  for (const f of files) {
    const parts = (f.folder || 'Other').split('/');
    let node = root;
    for (const part of parts) {
      let child = node.children.find(c => c.name === part);
      if (!child) {
        child = { name: part, path: [...(node.path ? [node.path] : []), part].join('/'), files: [], children: [] };
        node.children.push(child);
      }
      node = child;
    }
    node.files.push(f);
  }
  // Sort: folders first, then files; folders alphabetically
  const sortNode = (n: TreeNode) => {
    n.children.sort((a, b) => a.name.localeCompare(b.name));
    n.children.forEach(sortNode);
  };
  sortNode(root);
  return root;
}

function FolderTree({ node, depth, expanded, toggle, onFileAction, onSetDirection, onDelete }: {
  node: TreeNode; depth: number; expanded: Set<string>; toggle: (p: string) => void;
  onFileAction: (action: string, f: FileItem) => void;
  onSetDirection: (id: string, direction: string) => void;
  onDelete: (f: FileItem) => void;
}) {
  const { t } = useTranslation();
  const isExpanded = expanded.has(node.path) || depth === 0;
  const hasContent = node.children.length > 0 || node.files.length > 0;

  return (
    <div>
      {depth > 0 && hasContent && (
        <button onClick={() => toggle(node.path)}
          className="flex items-center gap-2 w-full text-left hover:bg-muted/50 rounded-md px-2 py-1.5"
          style={{ paddingLeft: `${depth * 16 + 8}px` }}>
          {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
          {isExpanded ? <FolderOpen className="h-4 w-4 text-amber-500 shrink-0" /> : <Folder className="h-4 w-4 text-amber-500 shrink-0" />}
          <span className="text-sm font-medium">{node.name}</span>
          <span className="text-xs text-muted-foreground">({node.files.length})</span>
        </button>
      )}
      {isExpanded && (
        <>
          {node.children.map(child => (
            <FolderTree key={child.path} node={child} depth={depth + 1} expanded={expanded} toggle={toggle} onFileAction={onFileAction} onSetDirection={onSetDirection} onDelete={onDelete} />
          ))}
          {node.files.map(f => (
            <div key={f.id} className="flex items-center justify-between hover:bg-muted/30 rounded-md px-2 py-1.5"
              style={{ paddingLeft: `${(depth + 1) * 16 + 8}px` }}>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {fileIcon(f.file_type)}
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{f.filename || f.original_name}</div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{formatSize(f.file_size || 0)}</span>
                    <span>{f.created_at?.slice(0, 10)}</span>
                    {f.category === 'invoice' && f.direction && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        f.direction === 'outgoing' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                      }`}>{f.direction === 'outgoing' ? tr('Sales', '銷售', '销售') : tr('Purchase', '採購', '采购')}</span>
                    )}
                    {f.category === 'invoice' && f.payment_status && f.payment_status !== 'unmatched' && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        f.payment_status === 'received' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        : f.payment_status === 'paid' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                        : 'bg-gray-100 text-gray-700'
                      }`}>{f.payment_status === 'received' ? tr('Received', '已收', '已收') : f.payment_status === 'paid' ? tr('Paid', '已付', '已付') : f.payment_status}</span>
                    )}
                    {f.category === 'invoice' && f.amount != null && (
                      <span className="font-mono">${f.amount.toLocaleString()}</span>
                    )}
                    {f.description && <span className="truncate max-w-[200px]">— {f.description}</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-1 ml-2 shrink-0">
                {f.category === 'invoice' && (
                  <button onClick={() => onSetDirection(f.id, f.direction === 'outgoing' ? 'incoming' : 'outgoing')}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                      f.direction === 'outgoing' ? 'border-blue-300 text-blue-600 hover:bg-blue-50' :
                      f.direction === 'incoming' ? 'border-orange-300 text-orange-600 hover:bg-orange-50' :
                      'border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`} title={tr('Toggle Sales/Purchase', '切換銷售/採購', '切换销售/采购')}>
                    {!f.direction ? '?' : f.direction === 'outgoing' ? tr('S', '銷', '销') : tr('P', '採', '采')}
                  </button>
                )}
                <button onClick={() => downloadFile(f.id, f.filename || 'file')} className="p-1 hover:bg-muted rounded"><Download className="h-3.5 w-3.5" /></button>
                <button onClick={() => onFileAction('edit', f)} className="p-1 hover:bg-muted rounded"><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => onDelete(f)} className="p-1 hover:bg-muted rounded text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

export default function FileStorage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === 'staff' || user?.role === 'viewer';
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [processingMsg, setProcessingMsg] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [folder, setFolder] = useState('');
  const [description, setDescription] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [filterFolder, setFilterFolder] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editFolder, setEditFolder] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [supModal, setSupModal] = useState<{ show: boolean; onConfirm: () => void } | null>(null);
  // Issue 17: type-choice modal shown when AI can't confidently decide document type
  const [typeChoice, setTypeChoice] = useState<{
    show: boolean;
    fileId: string;
    filename: string;
    bankScore: number;
    invoiceScore: number;
  } | null>(null);
  // Duplicate bank statement popup
  const [dupWarning, setDupWarning] = useState<{
    show: boolean;
    fileId: string;
    bankName: string | null;
    period: string | null;
    existingFileName: string | null;
    statementId: string | null;
    invoiceId: string | null;       // for invoice/receipt duplicates
    dupType: 'bank_statement' | 'invoice' | 'receipt' | 'file' | null;
    dupNumber: string | null;       // e.g. "2025001" or folder name
    dupVendor: string | null;       // or upload date
    pendingFile?: File | null;
  } | null>(null);

  const { data: files, isLoading } = useQuery({
    queryKey: ['file-storage', filterFolder, searchQ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filterFolder) params.set('folder', filterFolder);
      if (searchQ) params.set('q', searchQ);
      const qs = params.toString();
      return api(`/file-storage${qs ? `?${qs}` : ''}`);
    },
  });

  const { data: folders } = useQuery({
    queryKey: ['file-storage-folders'],
    queryFn: () => api('/file-storage/folders'),
  });

  const uploadMut = useMutation({
    mutationFn: (body: unknown) => api('/file-storage/upload', { method: 'POST', body, baseUrl: WORKER_API_BASE }),
    onSuccess: async (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['file-storage'] });
      queryClient.invalidateQueries({ queryKey: ['file-storage-folders'] });
      setDescription('');
      // Auto-import as bank statement → redirect to review page
      const fileId = data?.id;
      if (!fileId) {
        setUploading(false);
        return;
      }
      try {
        setProcessingMsg('Running OCR and detecting document type… (this may take 20–40 seconds)');

        // Use raw fetch so we can handle 409 (duplicate) without the api() helper throwing
        const token = localStorage.getItem('token') || '';
        const activeClient = localStorage.getItem('activeClient');
        const importHeaders: Record<string, string> = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        };
        if (activeClient) {
          try { const c = JSON.parse(activeClient); if (c?.id) importHeaders['X-Active-Client'] = c.id; } catch {}
        }
        const importResp = await fetch(
          `https://opcc-crm-api.ruhan-farhan.workers.dev/api/file-storage/${fileId}/import-document`,
          { method: 'POST', headers: importHeaders }
        );
        const result: any = await importResp.json();

        setProcessingMsg(null);
        setUploading(false);
        const docType = result?.type;
        const bankScore = result?.scores?.bankScore ?? 0;
        const invoiceScore = result?.scores?.invoiceScore ?? 0;
        const scoreDiff = Math.abs(bankScore - invoiceScore);

        // Duplicate bank statement detected (409)
        if ((importResp.status === 409 && (result?.type === 'bank_statement' || result?.error === 'Statement already imported')) || result?.error === 'Statement already imported') {
          setDupWarning({
            show: true,
            fileId,
            bankName: result.duplicate_info?.bank_name || null,
            period: result.duplicate_info?.period || null,
            existingFileName: result.duplicate_info?.file_name || null,
            statementId: result.statement_id || null,
            invoiceId: null,
            dupType: 'bank_statement',
            dupNumber: null,
            dupVendor: null,
          });
          return;
        }

        // Duplicate invoice or receipt detected (409)
        if (importResp.status === 409 && result?.type !== 'bank_statement') {
          setDupWarning({
            show: true,
            fileId,
            bankName: null,
            period: null,
            existingFileName: null,
            statementId: null,
            invoiceId: result.invoice_id || null,
            dupType: result.duplicate_info?.type || (result?.error?.toLowerCase().includes('receipt') ? 'receipt' : 'invoice'),
            dupNumber: result.duplicate_info?.number || null,
            dupVendor: result.duplicate_info?.vendor || null,
          });
          return;
        }

        // Only show type-choice popup if BOTH scores are non-zero and genuinely tied.
        // Filename pre-scoring on the backend means score 0/0 never happens for known formats.
        if (bankScore > 0 && invoiceScore > 0 && scoreDiff < 2) {
          setTypeChoice({
            show: true,
            fileId,
            filename: data?.filename || data?.original_name || 'this file',
            bankScore,
            invoiceScore,
          });
          return;
        }

        if (docType === 'bank_statement' && result?.statement_id) {
          if (result?.ocr_failed) {
            toast.warning('The system could not automatically read this file. You will be taken to the review page where you can enter details manually.');
          }
          navigate(`/bank-statements/review/${result.statement_id}`);
        } else if (docType === 'invoice' && result?.invoice_id) {
          if (result?.ocr_failed) {
            toast.warning('Could not automatically read this invoice. You will be taken to the review page to enter details manually.');
          }
          // Navigate to Invoice Review page — same flow as bank statements
          navigate(`/invoices/review/${result.invoice_id}`);
        } else if (result?.error) {
          toast.error(`Could not auto-process: ${result.error}`);
        }
      } catch (err: any) {
        setProcessingMsg(null);
        setUploading(false);
        toast.error(`Could not process file: ${err?.message || 'Unknown error'}`);
      }
    },
    onError: (err: any) => {
      setProcessingMsg(null);
      toast.error(`Upload failed: ${err?.message || err?.error || 'Unknown error'}`);
      setUploading(false);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api(`/file-storage/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['file-storage'] });
      queryClient.invalidateQueries({ queryKey: ['file-storage-folders'] });
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) => api(`/file-storage/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['file-storage'] });
      queryClient.invalidateQueries({ queryKey: ['file-storage-folders'] });
      setEditingId(null);
    },
  });

  const autoMatchMut = useMutation({
    mutationFn: () => api('/file-storage/auto-match-invoices', { method: 'POST' }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['file-storage'] });
      toast.success(tr(`Match complete: ${data.matched?.length || 0} matched, ${data.unmatched || 0} unmatched`, `配對完成：${data.matched?.length || 0} 筆成功，${data.unmatched || 0} 筆未配對`, `配对完成：${data.matched?.length || 0} 笔成功，${data.unmatched || 0} 笔未配对`));
    },
  });

  const importStmtMut = useMutation({
    mutationFn: (id: string) => api(`/file-storage/${id}/import-statement`, { method: 'POST' }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['file-storage'] });
      queryClient.invalidateQueries({ queryKey: ['file-storage-folders'] });
      queryClient.invalidateQueries({ queryKey: ['bank-statements'] });
      toast.success(tr(`Bank statement imported!\nTransactions: ${data.transactions_count || 0}\nBank: ${data.bank_name || 'Unknown'}`, `已匯入銀行月結單！\n交易筆數：${data.transactions_count || 0}\n銀行：${data.bank_name || '未知'}`, `已汇入银行月结单！\n交易笔数：${data.transactions_count || 0}\n银行：${data.bank_name || '未知'}`));
    },
    onError: (err: any) => {
      toast.error(tr(`Import failed: ${err?.message || err?.error || 'Unknown error'}`, `匯入失敗：${err?.message || err?.error || '未知錯誤'}`, `汇入失败：${err?.message || err?.error || '未知错误'}`));
    },
  });

  const uploadFiles = useCallback(async (fileList: FileList | File[]) => {
    const arr = Array.from(fileList);
    if (arr.length === 0) return;

    for (const file of arr) {
      // ── General duplicate check: same filename already in system ──────
      // Skip for PDFs, images, and Excel files — they go through auto-processing
      // which has its own better duplicate detection (by invoice number, bank period, etc.)
      const lowerName = file.name.toLowerCase();
      const isAutoProcessable = /\.(pdf|jpg|jpeg|png|gif|webp|bmp|xlsx|xls|csv)$/i.test(lowerName);

      if (!isAutoProcessable) {
        try {
          const dupCheck: any = await api(`/file-storage/check-duplicate?filename=${encodeURIComponent(file.name)}`);
          if (dupCheck?.exists && dupCheck?.existing_file) {
            const ef = dupCheck.existing_file;
            setDupWarning({
              show: true,
              fileId: '',
              bankName: null,
              period: null,
              existingFileName: ef.filename || ef.original_name,
              statementId: null,
              invoiceId: null,
              dupType: 'file',
              dupNumber: ef.folder || 'Uploads',
              dupVendor: ef.created_at?.slice(0, 10) || '',
              pendingFile: file,
            });
            return; // modal handles the rest via handleDupChoice
          }
        } catch { /* if check fails, proceed with upload */ }
      }

      // ── Pre-upload duplicate check for bank statements ──────────────
      const isBankStatement =
        /hsbc|hang.?seng|bank.?of.?china|boc|standard.?chartered|citibank|dbs|statement/i.test(lowerName) &&
        /\.(pdf|jpg|jpeg|png)$/i.test(lowerName);

      if (isBankStatement) {
        // Extract bank name and period from filename
        const bankMatch = /hsbc|hang.?seng|boc|standard.?chartered|citibank|dbs/i.exec(lowerName);
        const periodMatch = /(20\d{2})[_\-]?(0[1-9]|1[0-2])/i.exec(file.name);

        if (periodMatch) {
          const year = parseInt(periodMatch[1]);
          const month = parseInt(periodMatch[2]);
          // Fetch existing statements and check for same period + bank
          try {
            const token = localStorage.getItem('token') || '';
            const activeClient = localStorage.getItem('activeClient');
            const headers: Record<string, string> = { 'Authorization': `Bearer ${token}` };
            if (activeClient) {
              try { const cl = JSON.parse(activeClient); if (cl?.id) headers['X-Active-Client'] = cl.id; } catch {}
            }
            const resp = await fetch(
              `https://opcc-crm-api.ruhan-farhan.workers.dev/api/bank-statements?show_drafts=1`,
              { headers }
            );
            if (resp.ok) {
              const data: any = await resp.json();
              const existing = (data.data || []).find((s: any) =>
                s.statement_year === year &&
                s.statement_month === month &&
                (!bankMatch || (s.bank_name || '').toUpperCase().includes(bankMatch[0].toUpperCase()))
              );
              if (existing) {
                // Show duplicate popup — don't upload yet
                setDupWarning({
                  show: true,
                  fileId: '',           // empty — file not uploaded yet
                  bankName: existing.bank_name,
                  period: `${year}-${String(month).padStart(2, '0')}`,
                  existingFileName: existing.file_name,
                  statementId: existing.id,
                  invoiceId: null,
                  dupType: 'bank_statement',
                  dupNumber: null,
                  dupVendor: null,
                  pendingFile: file,    // keep the file to upload if user says Yes
                });
                return; // stop — wait for user choice
              }
            }
          } catch { /* if check fails, proceed with upload normally */ }
        }
      }
      // ── No duplicate found — proceed with upload ─────────────────────
      await doUpload(file);
    }
  }, [folder, description, uploadMut]);

  // Extracted upload logic — reads file as base64 and calls uploadMut
  const doUpload = useCallback((file: File) => {
    return new Promise<void>((resolve) => {
      // Check file size — iOS Safari struggles with files > 20MB
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 20MB.`);
        setUploading(false);
        resolve();
        return;
      }
      setUploading(true);
      const reader = new FileReader();
      reader.onload = (ev) => {
        const base64 = ev.target?.result as string;
        if (!base64) {
          toast.error('Could not read file. Please ensure the file is fully downloaded before uploading.');
          setUploading(false);
          resolve();
          return;
        }
        const autoFolderName = folder || autoFolder(file.name, file.type);
        uploadMut.mutate({
          filename: file.name,
          original_name: file.name,
          file_type: file.type || 'application/octet-stream',
          file_size: file.size,
          file_data: base64,
          folder: autoFolderName,
          description,
        });
        resolve();
      };
      reader.onerror = () => {
        toast.error('Upload failed: Could not read the file. Make sure it is fully downloaded locally.');
        setUploading(false);
        resolve();
      };
      reader.readAsDataURL(file);
    });
  }, [folder, description, uploadMut]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) uploadFiles(e.target.files);
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
  const handleDrop = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); if (e.dataTransfer.files.length > 0) uploadFiles(e.dataTransfer.files); };

  const toggleFolder = (path: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };

  const handleFileAction = (action: string, f: FileItem) => {
    if (action === 'edit') {
      setEditingId(f.id);
      setEditName(f.filename || '');
      setEditFolder(f.folder || '');
      setEditDesc(f.description || '');
    } else if (action === 'delete') {
      deleteMut.mutate(f.id);
    } else if (action === 'import-statement') {
      if (confirm(tr(`Import "${f.filename}" as a bank statement? The system will auto-OCR and parse transactions.`, `確定要將「${f.filename}」匯入為銀行月結單嗎？系統會自動 OCR 辨識並解析交易紀錄。`, `确定要将「${f.filename}」汇入为银行月结单吗？系统会自动 OCR 辨识并解析交易纪录。`))) {
        importStmtMut.mutate(f.id);
      }
    }
  };

  const directionMut = useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: string }) =>
      api(`/file-storage/${id}/direction`, { method: 'PATCH', body: JSON.stringify({ direction }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['file-storage'] }),
  });

  const handleSetDirection = (id: string, direction: string) => {
    directionMut.mutate({ id, direction });
  };

  // Handle duplicate warning response
  const handleDupChoice = async (reupload: boolean) => {
    if (!dupWarning) return;
    const { fileId, statementId, invoiceId, dupType, pendingFile } = dupWarning;
    setDupWarning(null);

    if (!reupload) {
      // User said No — navigate to the existing record to view it
      if (dupType === 'bank_statement' && statementId) {
        navigate(`/bank-statements/review/${statementId}`);
      } else if (dupType === 'file') {
        // Just close — user decided not to re-upload
        return;
      } else if (invoiceId) {
        navigate(`/invoices/review/${invoiceId}`);
      }
      return;
    }

    // User said Yes — re-upload/re-import
    if (pendingFile) {
      // Pre-upload duplicate — just proceed with the upload normally
      await doUpload(pendingFile);
      return;
    }

    // Post-upload duplicate — re-import with force=true (no duplicate check, clean slate)
    setProcessingMsg('Re-importing file…');
    try {
      const token = localStorage.getItem('token') || '';
      const activeClient = localStorage.getItem('activeClient');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      if (activeClient) {
        try { const c = JSON.parse(activeClient); if (c?.id) headers['X-Active-Client'] = c.id; } catch {}
      }
      const resp = await fetch(
        `https://opcc-crm-api.ruhan-farhan.workers.dev/api/file-storage/${fileId}/import-document?force=true`,
        { method: 'POST', headers }
      );
      const result: any = await resp.json();
      setProcessingMsg(null);
      if (result?.statement_id) {
        navigate(`/bank-statements/review/${result.statement_id}`);
      } else if (result?.invoice_id) {
        navigate(`/invoices/review/${result.invoice_id}`);
      } else {
        toast.error(`Re-import failed: ${result?.error || 'Unknown error'}`);
      }
    } catch (err: any) {
      setProcessingMsg(null);
      toast.error(`Re-import failed: ${err?.message || 'Unknown error'}`);
    }
  };

  const fileList = (files?.data || []) as FileItem[];
  const folderList = (folders?.data || []) as string[];
  const tree = useMemo(() => buildTree(fileList), [fileList]);

  // Issue 17: handle user's manual type selection
  const handleTypeChoice = async (choice: 'bank_statement' | 'invoice' | 'store') => {
    if (!typeChoice) return;
    const { fileId, filename } = typeChoice;
    setTypeChoice(null);

    if (choice === 'store') {
      // Just keep in file storage, no processing
      queryClient.invalidateQueries({ queryKey: ['file-storage'] });
      return;
    }

    setProcessingMsg(choice === 'bank_statement'
      ? 'Processing as bank statement… (20–40 seconds)'
      : 'Processing as invoice… (20–40 seconds)');

    try {
      const endpoint = choice === 'bank_statement'
        ? `/file-storage/${fileId}/import-statement`
        : `/file-storage/${fileId}/import-invoice`;
      const result: any = await api(endpoint, { method: 'POST' });
      setProcessingMsg(null);

      if (choice === 'bank_statement' && result?.statement_id) {
        navigate(`/bank-statements/review/${result.statement_id}`);
      } else if (choice === 'invoice' && result?.invoice_id) {
        if (result?.ocr_failed) {
          toast.warning('Could not automatically read this invoice. You will be taken to the review page to enter details manually.');
        }
        navigate(`/invoices/review/${result.invoice_id}`);
      } else if (result?.error) {
        toast.error(`Processing failed: ${result.error}`);
      }
    } catch (err: any) {
      setProcessingMsg(null);
      toast.error(`Processing failed: ${err?.message || 'Unknown error'}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Processing overlay */}
      {processingMsg && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-card rounded-lg p-8 max-w-md mx-4 text-center shadow-2xl">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4"></div>
            <h3 className="font-bold text-lg mb-2">Processing your file…</h3>
            <p className="text-sm text-muted-foreground">{processingMsg}</p>
            <p className="text-xs text-muted-foreground mt-4">You'll be taken to the review page when it's ready.</p>
          </div>
        </div>
      )}

      {/* Supervisor password modal for staff delete */}
      {supModal?.show && (
        <SupervisorPasswordModal
          action="delete this file"
          onConfirm={supModal.onConfirm}
          onCancel={() => setSupModal(null)}
        />
      )}

      {/* Duplicate document warning popup — handles bank statements, invoices, and receipts */}
      {dupWarning?.show && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-card rounded-lg p-6 max-w-sm mx-4 shadow-2xl">
            <div className="text-center mb-4">
              <div className="text-3xl mb-2">⚠️</div>
              {dupWarning.dupType === 'bank_statement' ? (
                <>
                  <h3 className="font-bold text-lg">Bank Statement Already Exists</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    {dupWarning.bankName && dupWarning.period
                      ? <>A <strong>{dupWarning.bankName}</strong> statement for <strong>{dupWarning.period}</strong> has already been uploaded and processed.</>
                      : <>This bank statement has already been uploaded and processed.</>
                    }
                  </p>
                  {dupWarning.existingFileName && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Existing file: <span className="font-mono">{dupWarning.existingFileName}</span>
                    </p>
                  )}
                </>
              ) : dupWarning.dupType === 'receipt' ? (
                <>
                  <h3 className="font-bold text-lg">Receipt Already Exists 收據已存在</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Receipt <strong>#{dupWarning.dupNumber}</strong>
                    {dupWarning.dupVendor ? <> from <strong>{dupWarning.dupVendor}</strong></> : ''} has already been imported.
                  </p>
                </>
              ) : dupWarning.dupType === 'file' ? (
                <>
                  <h3 className="font-bold text-lg">{tr('File Already Exists', '檔案已存在', '文件已存在')}</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    {tr('A file named', '名為', '名为')} <strong>{dupWarning.existingFileName}</strong> {tr('already exists in folder', '已存在於資料夾', '已存在于文件夹')} <strong>{dupWarning.dupNumber}</strong>.
                  </p>
                  {dupWarning.dupVendor && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {tr('Uploaded on', '上傳日期', '上传日期')}: {dupWarning.dupVendor}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <h3 className="font-bold text-lg">Invoice Already Exists 發票已存在</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Invoice <strong>#{dupWarning.dupNumber}</strong>
                    {dupWarning.dupVendor ? <> for <strong>{dupWarning.dupVendor}</strong></> : ''} has already been imported.
                  </p>
                </>
              )}
              <p className="text-sm font-medium mt-3">Do you want to upload it again?</p>
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => handleDupChoice(true)}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90"
              >
                Yes, upload again
              </button>
              <button
                onClick={() => handleDupChoice(false)}
                className="px-6 py-2 border border-border rounded-md text-sm font-medium hover:bg-muted"
              >
                {dupWarning.dupType === 'file' ? tr('Cancel', '取消', '取消') : 'No, view existing'}
              </button>
            </div>
          </div>
        </div>
      )}
      {typeChoice?.show && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-card rounded-lg p-6 max-w-sm mx-4 shadow-2xl">
            <div className="text-center mb-4">
              <div className="text-3xl mb-2">🤔</div>
              <h3 className="font-bold text-lg">What type of document is this?</h3>
              <p className="text-sm text-muted-foreground mt-1">
                The system couldn't automatically determine the type of <strong>{typeChoice.filename}</strong>. Please choose:
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => handleTypeChoice('bank_statement')}
                className="w-full flex items-center gap-3 px-4 py-3 border-2 border-blue-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/30 text-left transition-colors"
              >
                <span className="text-2xl">🏦</span>
                <div>
                  <div className="font-medium text-sm">Bank Statement</div>
                  <div className="text-xs text-muted-foreground">Extract transactions and reconcile</div>
                </div>
              </button>
              <button
                onClick={() => handleTypeChoice('invoice')}
                className="w-full flex items-center gap-3 px-4 py-3 border-2 border-green-200 rounded-lg hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-950/30 text-left transition-colors"
              >
                <span className="text-2xl">🧾</span>
                <div>
                  <div className="font-medium text-sm">Invoice / Receipt</div>
                  <div className="text-xs text-muted-foreground">Extract invoice details and match to payments</div>
                </div>
              </button>
              <button
                onClick={() => handleTypeChoice('store')}
                className="w-full flex items-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-lg hover:border-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900/30 text-left transition-colors"
              >
                <span className="text-2xl">📁</span>
                <div>
                  <div className="font-medium text-sm">Just store it</div>
                  <div className="text-xs text-muted-foreground">Keep in File Storage without processing</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        <h2 className="text-2xl font-bold">{t('fileStorage.title')}</h2>
        <p className="text-muted-foreground mt-1">{t('fileStorage.desc')}</p>
      </div>

      {/* Upload area */}
      <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
        className={`bg-card border-2 border-dashed rounded-xl p-8 transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border'}`}>
        <div className="flex flex-col items-center gap-4">
          <div className={`rounded-full p-4 transition-colors ${dragOver ? 'bg-primary/10' : 'bg-muted'}`}>
            <Upload className={`h-8 w-8 ${dragOver ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>
          <div className="text-center">
            <p className="font-medium">{dragOver ? t('fileStorage.dropHere') : t('fileStorage.dragDrop')}</p>
            <p className="text-sm text-muted-foreground mt-1">{t('fileStorage.orClick')}</p>
          </div>
          <label className="cursor-pointer bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-medium hover:opacity-90">
            {uploading ? 'Uploading...' : t('fileStorage.upload')}
            <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.csv,.txt,.ppt,.pptx,.zip" onChange={handleFileInput} className="hidden" multiple />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t">
          <div>
            <label className="text-xs text-muted-foreground">{t('fileStorage.folder')}</label>
            <input value={folder} onChange={e => setFolder(e.target.value)} placeholder={t('fileStorage.folderPlaceholder')}
              className="px-3 py-2 border rounded-md bg-background text-sm w-52" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-muted-foreground">{t('fileStorage.description')}</label>
            <input value={description} onChange={e => setDescription(e.target.value)} placeholder={t('fileStorage.description')}
              className="px-3 py-2 border rounded-md bg-background text-sm w-full" />
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder={t('fileStorage.search')}
            className="pl-9 pr-3 py-2 border rounded-md bg-background text-sm w-full" />
        </div>
        <select value={filterFolder} onChange={e => setFilterFolder(e.target.value)}
          className="px-3 py-2 border rounded-md bg-background text-sm min-w-[160px]">
          <option value="">{t('fileStorage.allFolders')}</option>
          {folderList.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <span className="text-xs text-muted-foreground">
          {tr(`${fileList.length} file${fileList.length === 1 ? '' : 's'}`, `${fileList.length} 個檔案`, `${fileList.length} 个档案`)}
        </span>
        <button onClick={() => autoMatchMut.mutate()} disabled={autoMatchMut.isPending}
          className="flex items-center gap-1 px-3 py-2 bg-primary text-primary-foreground rounded-md text-xs hover:opacity-90 disabled:opacity-40">
          <Zap className="h-3 w-3" /> {tr('Match Invoices', '配對發票', '配对发票')}
        </button>
      </div>

      {/* Folder Tree View */}
      {editingId ? (
        <div className="bg-card border rounded-xl p-6">
          <div className="space-y-3">
            <input value={editName} onChange={e => setEditName(e.target.value)} className="px-3 py-2 border rounded-md text-sm w-full"
              placeholder={tr('Filename', '檔案名稱', '档案名称')} />
            <div className="flex gap-3">
              <input value={editFolder} onChange={e => setEditFolder(e.target.value)} className="px-3 py-2 border rounded-md text-sm flex-1"
                placeholder={tr('Folder (use / for subfolders)', '資料夾（可用 / 分隔層級）', '资料夹（可用 / 分隔層級）')} />
              <input value={editDesc} onChange={e => setEditDesc(e.target.value)} className="px-3 py-2 border rounded-md text-sm flex-1"
                placeholder={tr('Description', '描述', '描述')} />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditingId(null)} className="px-3 py-1.5 border rounded-md text-sm">
                {tr('Cancel', '取消', '取消')}
              </button>
              <button onClick={() => updateMut.mutate({ id: editingId, body: { filename: editName, folder: editFolder, description: editDesc } })}
                className="px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm">
                {tr('Save', '儲存', '储存')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="bg-card border rounded-xl p-4">
        {isLoading ? (
          <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>
        ) : fileList.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">{t('fileStorage.noData')}</p>
        ) : (
          <FolderTree node={tree} depth={0} expanded={expanded} toggle={toggleFolder} onFileAction={handleFileAction} onSetDirection={handleSetDirection} onDelete={(f) => {
            if (isStaff) {
              setSupModal({ show: true, onConfirm: () => handleFileAction('delete', f) });
            } else {
              if (confirm(t('common.confirmDelete'))) handleFileAction('delete', f);
            }
          }} />
        )}
      </div>
    </div>
  );
}
